import loc from 'owa-localize';
import * as React from 'react';
import { PrimaryButton, IButtonStyles } from '@fluentui/react/lib/Button';
import { composeSendButton } from 'owa-locstrings/lib/strings/composesendbutton.locstring.json';
import { saveAsDraft } from './draft.locstring.json';
import styles from './DraftSplitButton.scss';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { DefaultPalette } from '@fluentui/style-utilities/lib/index';
import type { ControlIcons } from 'owa-control-icons';
import { getPalette } from 'owa-theme';

export interface DraftSplitButtonProps extends React.HTMLProps<HTMLDivElement> {
    caption: string;
    onSend: () => void;
    onSaveAsDraft: () => void;
    colors?: ColorScheme;
    icon?: ControlIcons;
}
export default function DraftSplitButton(props: DraftSplitButtonProps) {
    let { caption, icon, colors } = props;
    let customStyle = calculateButtonStyle(colors);

    const buttonProps = {
        split: true,
        menuProps: {
            items: [
                {
                    key: 'RegularSendMessage',
                    text: loc(composeSendButton),
                    onClick: props.onSend,
                },
                {
                    key: 'DraftEventMessage',
                    text: loc(saveAsDraft),
                    onClick: props.onSaveAsDraft,
                },
            ],
        },
    };

    return (
        <div className={styles.sendButton}>
            <PrimaryButton
                {...buttonProps}
                styles={customStyle}
                iconProps={{
                    iconName: icon,
                }}
                onClick={props.onSend}
                disabled={props.disabled}>
                {caption}
            </PrimaryButton>
        </div>
    );
}

function calculateButtonStyle(colors: ColorScheme): IButtonStyles {
    let themeSymbols = getPalette();
    let rootColor = colors ? colors.calPrimary : themeSymbols.themePrimary;
    let hoverColor = colors ? colors.calDarkAlt : themeSymbols.themeDarkAlt;
    let pressedColor = colors ? colors.calDark : themeSymbols.themeDark;
    let focusBorderColor = colors ? colors.calDarkAlt : themeSymbols.themeDarker;
    let textColor = colors ? colors.accessibleText.calPrimaryBackground : DefaultPalette.white;
    let customStyle: IButtonStyles = {
        root: {
            minWidth: 80,
            height: 32,
            backgroundColor: rootColor,
            color: textColor,
            fontWeight: 600,
        },
        icon: {
            color: textColor,
        },
        splitButtonContainer: {
            backgroundColor: rootColor,
        },
        splitButtonMenuButtonExpanded: {
            backgroundColor: pressedColor,
            selectors: {
                ':hover': {
                    backgroundColor: hoverColor,
                },
            },
        },
        splitButtonDivider: {
            color: textColor,
        },
        splitButtonMenuButton: {
            backgroundColor: rootColor,
            selectors: {
                ':hover': {
                    backgroundColor: hoverColor,
                },
            },
        },
        splitButtonMenuIcon: {
            color: textColor,
            justifyContent: 'center',
        },
        iconHovered: {
            color: textColor,
        },
        iconPressed: {
            color: textColor,
        },
        iconDisabled: {
            color: DefaultPalette.neutralTertiary,
        },
        flexContainer: {
            justifyContent: 'center',
        },
        rootHovered: {
            backgroundColor: hoverColor,
            color: textColor,
        },
        rootPressed: {
            backgroundColor: pressedColor,
            color: textColor,
            border: 'none', // To avoid showing focused border on press
            boxShadow: 'none',
        },
        rootFocused: {
            border: 'solid 1px ' + DefaultPalette.white, // Inner border
            boxShadow: '0 0 0 1px ' + focusBorderColor, // Outer border
            color: textColor,
        },
        rootDisabled: {
            backgroundColor: DefaultPalette.neutralLight,
            color: DefaultPalette.neutralTertiary,
        },
    };
    return customStyle;
}
